import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Bell } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NoticesPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [notices, setNotices] = useState([]);
  const [classes, setClasses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    target_roles: [],
    target_classes: []
  });

  // Only Admin can create notices - Teacher can only view
  const canCreateNotice = user?.role === 'school_admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchNotices();
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() });
      setClasses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  const fetchNotices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notices`, {
        headers: getAuthHeader()
      });
      setNotices(response.data);
    } catch (error) {
      console.error('Failed to fetch notices:', error);
      toast.error('Failed to load notices');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/notices`, formData, {
        headers: getAuthHeader()
      });
      toast.success('Notice posted successfully');
      setDialogOpen(false);
      setFormData({ title: '', content: '', target_roles: [], target_classes: [] });
      fetchNotices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post notice');
    }
  };

  const toggleRole = (role) => {
    setFormData(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  const toggleClass = (classId) => {
    setFormData(prev => ({
      ...prev,
      target_classes: prev.target_classes.includes(classId)
        ? prev.target_classes.filter(c => c !== classId)
        : [...prev.target_classes, classId]
    }));
  };

  return (
    <DashboardLayout title="Notices">
      <div className="space-y-6">
        <div className="flex justify-end">
          {canCreateNotice && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-notice-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Notice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create New Notice</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      data-testid="title-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="content">Content *</Label>
                    <Textarea
                      id="content"
                      rows={5}
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      data-testid="content-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Audience</Label>
                    <div className="flex flex-wrap gap-2">
                      {['school_admin', 'teacher', 'student', 'parent'].map(role => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => toggleRole(role)}
                          data-testid={`role-${role}-btn`}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            formData.target_roles.includes(role)
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {role.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Leave empty to send to all roles</p>
                  </div>
                  
                  {/* Class-wise targeting */}
                  <div className="space-y-2">
                    <Label>Target Classes (Optional)</Label>
                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto p-2 border rounded-md">
                      {classes.map(cls => (
                        <button
                          key={cls.id}
                          type="button"
                          onClick={() => toggleClass(cls.id)}
                          data-testid={`class-${cls.id}-btn`}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            formData.target_classes.includes(cls.id)
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {cls.name}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Select specific classes or leave empty for all classes</p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-notice-btn">Post Notice</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="space-y-4">
          {notices.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">No notices found</p>
              </CardContent>
            </Card>
          ) : (
            notices.map(notice => (
              <Card key={notice.id} data-testid={`notice-item-${notice.id}`}>
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Bell className="h-6 w-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl">{notice.title}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {new Date(notice.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground whitespace-pre-wrap">{notice.content}</p>
                  {notice.target_roles && notice.target_roles.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {notice.target_roles.map(role => (
                        <span
                          key={role}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                        >
                          {role.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NoticesPage;
